import {
  useApiUrl,
  useDataProvider,
  useNavigation,
  useNotification,
  useUpdate,
} from "@refinedev/core";

import { notifyMutationError } from "@/lib/repository-utils";
import { GenresEditFormType } from "@/pages/genres/schemas/genres.edit.form";
export interface GenreSummary {
  id: string;
  name: string;
  metadata?: { source: string };
  deprecated: boolean;
}

export const useGenreRepository = () => {
  const { getOne, getList, custom } = useDataProvider()();
  const apiUrl = useApiUrl();
  const { mutateAsync: updateAsync } = useUpdate({ resource: "genres" });
  const { open } = useNotification();
  const { edit } = useNavigation();

  async function get(id: string): Promise<any> {
    const response = await getOne({ id, resource: "genres" });
    return response;
  }

  async function search(
    query: string,
  ): Promise<Array<{ id: string; name: string }>> {
    if (!query) {
      return [];
    }
    try {
      const response = await getList({
        resource: "genres",
        filters: [],
        pagination: {
          currentPage: 0,
          pageSize: 25,
        },
        meta: {
          sortBy: "_text_match:desc",
          query: query,
          queryBy: "transliterations.el,name",
        },
      });
      return response.data.map((genre: any) => ({
        id: genre.document.id,
        name: genre.document.name,
      }));
    } catch (error) {
      console.error("Genre search failed", error);
      notifyMutationError(open, "Genre search", error);
      return [];
    }
  }

  async function getUsageCount(
    id: string,
  ): Promise<{ events: number; artists: number }> {
    const eventsResult = await getList?.({
      resource: "events",
      pagination: {
        pageSize: 1,
        currentPage: 0,
      },
      filters: [
        {
          field: "genres.id",
          operator: "eq",
          value: id,
        },
      ],
      meta: {
        includeFields: ["id"],
      },
    });

    const artistsResult = await getList?.({
      resource: "artists",
      pagination: {
        pageSize: 1,
        currentPage: 0,
      },
      filters: [
        {
          field: "genres.id",
          operator: "eq",
          value: id,
        },
      ],
      meta: {
        includeFields: ["id"],
      },
    });

    return {
      events: eventsResult?.total || 0,
      artists: artistsResult?.total || 0,
    };
  }

  async function update(values: Partial<GenresEditFormType>) {
    const response = await updateAsync?.(
      {
        id: values.id!,
        values,
      },
      {
        onSuccess: (data) => {
          console.debug("Successful genre update", data);
          edit("genres", values.id!, "replace");
          open?.({
            type: "success",
            message: "Successfully updated genre",
            description: `You updated a genre called ${values.name ?? ""}`,
          });
        },
        onError: (error) => {
          console.error("Failed to update genre with values", values, error);
          notifyMutationError(open, "Update genre request", error);
        },
      },
    );

    return response.data.id?.toString()!;
  }

  async function merge(sourceId: string, targetId: string) {
    try {
      const response = await custom?.({
        url: `${apiUrl}/genres/merge`,
        method: "post",
        payload: {
          sourceGenreId: sourceId,
          targetGenreId: targetId,
        },
      });

      open?.({
        type: "success",
        message: "Successfully merged genres",
        description: (response?.data?.errors ?? []).join("\n"),
      });
    } catch (e: any) {
      console.error(e);
      open?.({
        type: "error",
        message: "Failed to merge genre",
        description: e?.response?.data?.detail,
      });
      throw e;
    }
  }

  async function nearestNeighbours(
    embedding: number[],
    limit: number,
  ): Promise<GenreSummary[]> {
    const response = await custom?.({
      url: `${apiUrl}/search/nearest-neighbours-by-embedding`,
      method: "post",
      payload: { Embedding: embedding, TargetType: "genre", Limit: limit },
    });
    return (response?.data as GenreSummary[]) ?? [];
  }

  return { get, search, getUsageCount, update, merge, nearestNeighbours };
};
